import { gql } from '@apollo/client'
import { Avatar, Badge, Popover } from '@farol-ds/react'
import {
  buildWhatsappRedirect,
  SEUS_DIREITOS_ARTIGOS_MESSAGE,
  SEUS_DIREITOS_DEFAULT_MESSAGE,
  SEUS_DIREITOS_NOTICIAS_MESSAGE,
} from '@jusbrasil-web/shared-seus-direitos'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useEffect, useState } from 'react'

import printStyles from '../../print.module.scss'
import { SeusDireitosWidget_ViewerFragment } from './__generated__/seus-direitos-widget.graphql'
import { getCollapsedWidgetCookie, setCollapsedWidgetCookie } from './helpers'
import styles from './seus-direitos-widget.module.scss'

const INIT_DELAY = 500
const POPOVER_DELAY = 5000
// [FIXMe] put image in public folder or use a image from cdn. @andersonba
const AVATAR_URL =
  'https://static.jusbr.com/libs/chatlib/chatbot/bots/legal-case/assets/1644d29bf38dceb252abc180f28d862d.jpg'
const AVATAR_SEUS_DIREITOS_URL =
  'https://jusbrasil-static.s3.us-east-1.amazonaws.com/web/seus-direitos/experiments/male_avatar.png'

interface SendMetricsType {
  (name: string, properties?: Record<string, unknown>): void
}

export interface SeusDireitosWidgetProps {
  enabled?: boolean
  viewer: SeusDireitosWidget_ViewerFragment | null
  className?: string
  page: string
  sendMetrics: SendMetricsType
}

export function SeusDireitosWidget(props: SeusDireitosWidgetProps) {
  const { viewer, enabled = true, className, page, sendMetrics } = props

  const classes = classNames(styles.root, className, printStyles.unprintable)
  const session = useUserSession()

  const [show, setShow] = useState(false)
  const [badgeCount, setBadgeCount] = useState(1)
  const [showPopover, setShowPopover] = useState(false)

  const avatarUrl = AVATAR_URL

  const messageCopy = 'Fale agora com um advogado online'

  function togglePopoverState(state: boolean) {
    setShowPopover(state)
    setBadgeCount(state ? 1 : 0)
  }

  function syncCollapsedCookie(state: boolean) {
    if (!state) {
      setCollapsedWidgetCookie(!!session?.pid)
    }
  }

  useEffect(() => {
    const initTimer = setTimeout(() => setShow(true), INIT_DELAY)
    let popoverTimer: NodeJS.Timeout

    const hasCollapsedCookie = !!getCollapsedWidgetCookie()
    if (hasCollapsedCookie) {
      togglePopoverState(false)
    } else {
      popoverTimer = setTimeout(() => togglePopoverState(true), POPOVER_DELAY)
    }

    return () => {
      clearTimeout(initTimer)
      clearTimeout(popoverTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (viewer?.isLawyer || viewer?.hasPaidPlan || viewer?.isUserInMrBigCNJIntegration) {
    return null
  }

  function onWidgetClick() {
    sendMetrics('SeusDireitos.CtaClick', { metadata: { copy: messageCopy, page } })
    togglePopoverState(false)
    syncCollapsedCookie(false)

    let message = SEUS_DIREITOS_DEFAULT_MESSAGE
    if (page === 'docview-artigo') {
      message = SEUS_DIREITOS_ARTIGOS_MESSAGE
    } else if (page === 'docview-noticia') {
      message = SEUS_DIREITOS_NOTICIAS_MESSAGE
    }

    const url = buildWhatsappRedirect({
      message,
      deviceId: session?.jdid,
      userId: session?.pid != null ? String(session.pid) : undefined,
    })
    window.open(url, '_blank')
  }

  return (
    <>
      <Head>
        <link rel="preload" href={avatarUrl} as="image" />
      </Head>

      {enabled && show && (
        <Popover
          open={showPopover}
          onOpenChange={(open) => {
            togglePopoverState(open)
            syncCollapsedCookie(open)
          }}
        >
          <button className={classes} onClick={onWidgetClick}>
            <Popover.Anchor className={styles.anchor} data-testid="seus-direitos-widget-anchor">
              <Badge count={badgeCount} type="secondary" overlap="circular">
                <Avatar
                  src={avatarUrl}
                  size="xl"
                  alt="Assistente Sofia"
                  className={styles.avatar}
                />
              </Badge>
            </Popover.Anchor>
          </button>

          <Popover.Content
            className={styles.popover}
            onClick={(e) => {
              if (e.target instanceof Element && e.target.closest('[data-close]')) {
                return
              }
              onWidgetClick()
            }}
          >
            <Popover.ContentClose className={styles.popoverClose} data-close />
            <Popover.ContentDescription
              className={styles.popoverDescription}
              data-testid="seus-direitos-widget-popover"
            >
              {messageCopy}
            </Popover.ContentDescription>
          </Popover.Content>
        </Popover>
      )}
    </>
  )
}

SeusDireitosWidget.fragments = {
  viewer: gql`
    fragment SeusDireitosWidget_viewer on Profile {
      hasPaidPlan
      isLawyer
      isUserInMrBigCNJIntegration
    }
  `,
}

export const SeusDireitosWidgetLazy = dynamic(() => Promise.resolve(SeusDireitosWidget), {
  ssr: false,
})
